/**
 * ISSUE-1385. The team's season stats with league ranks, one card per
 * section (Offense (Per Game), Defense (Per Game); Batting, Pitching), under
 * the standings facts on the Stats panel. v1 drew the same set in
 * teamdetail stats_ration_widget.dart: a stat, its value, and where it ranks
 * in the league.
 *
 * Each section is a `<dl>` like the standings card above it: the label is the
 * `<dt>`, the value and the rank sit in the `<dd>`, so a screen reader reads
 * "Points, 117.3, 2nd in the league" as one fact. Every string is the
 * server's; this component formats nothing.
 */
import type { ReactNode } from "react";

import type { typeTeamSeasonRankSection } from "./useTeamSeasonRanks";

export const TEAM_SEASON_RANKS_RANK_SUFFIX = " in the league";

export const TeamSeasonRanksCard = (props: {
  sections: readonly typeTeamSeasonRankSection[];
}): ReactNode => (
  <div className="flex flex-col gap-4" data-testid="team-season-ranks">
    {props.sections.map((section) => (
      <section
        key={section.sectionKey}
        data-testid={`team-season-ranks-${section.sectionKey}`}
        className="rounded-md border border-hairline bg-surface"
      >
        <h3 className="px-4 pt-3 pb-2 text-body-strong text-ink">
          {section.label}
        </h3>
        <dl className="m-0">
          {section.rows.map((row) => (
            <div
              key={row.rowKey}
              data-testid={`team-season-rank-row-${row.rowKey}`}
              className="flex min-h-touch-target items-center justify-between gap-3 border-t border-hairline px-4 py-2"
            >
              <dt className="min-w-0 text-body text-ink-secondary">
                {row.label}
              </dt>
              <dd className="m-0 flex shrink-0 items-baseline gap-2 tabular-nums">
                <span
                  data-testid={`team-season-rank-value-${row.rowKey}`}
                  className="text-body-strong text-ink"
                >
                  {row.displayValue}
                </span>
                {row.rankLabel !== null ? (
                  <span
                    data-testid={`team-season-rank-label-${row.rowKey}`}
                    className="min-w-9 text-right text-caption text-ink-secondary"
                  >
                    {row.rankLabel}
                    <span className="sr-only">
                      {TEAM_SEASON_RANKS_RANK_SUFFIX}
                    </span>
                  </span>
                ) : null}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    ))}
  </div>
);
